import type {
  MedRecent,
  PatientMedication,
  PatientRow,
  ProMedicationDto
} from "./complianceTypes";
import type { MedBasis } from "./complianceFilters";
import { daysSince } from "./complianceClock";
import type { MedicationOption } from "../categoryMetadata";

// PD tags from the runecore tag mapping, eg medication.pd.levodopa or medication.pd.non-levodopa.
const PD_TAG_PREFIX = "medication.pd.";

export function isPdTag(pdTag: string | null): boolean {
  return pdTag !== null && pdTag.startsWith(PD_TAG_PREFIX);
}

export function parseProMedication(m: ProMedicationDto): PatientMedication {
  return {
    itemId: m.item_id,
    runeClassification: m.rune_classification,
    isPd: isPdTag(m.pd_tag),
    scheduled: m.scheduled,
    lastLoggedAt: m.last_logged_at,
    lastAutologgedAt: m.last_autologged_at
  };
}

// Shared by the patient list and the sidebar, so they can't disagree on which medicines count.
export function inMedBasis(
  med: { runeClassification: string; isPd: boolean },
  basis: MedBasis,
  studySet: Set<string>
): boolean {
  if (basis === "all") return true;
  if (basis === "pd") return med.isPd;
  return studySet.has(med.runeClassification);
}

// Study drugs come from the project's configuration; PD medicines have a PD tag. The sets
// overlap rather than nest.
export function medicationsForBasis(
  medications: PatientMedication[],
  basis: MedBasis,
  studyMedications: string[]
): PatientMedication[] {
  if (basis === "all") return medications;
  const studySet = new Set(studyMedications);
  return medications.filter((m) => inMedBasis(m, basis, studySet));
}

function latest(timestamps: (string | null)[]): string | null {
  let best: string | null = null;
  for (const t of timestamps) {
    if (t !== null && (best === null || Date.parse(t) > Date.parse(best))) {
      best = t;
    }
  }
  return best;
}

export function medRecentFor(
  patient: Pick<PatientRow, "medications" | "timezone">,
  basis: MedBasis,
  studyMedications: string[],
  now = new Date()
): MedRecent {
  const meds = medicationsForBasis(
    patient.medications,
    basis,
    studyMedications
  );
  // Recency comes from scheduled medicines only, so a recent as-needed dose of another
  // medicine can't hide a scheduled medicine that's gone silent. As-needed logs of a
  // scheduled medicine still count.
  const scheduled = meds.filter((m) => m.scheduled);
  const lastLoggedAt = latest(scheduled.map((m) => m.lastLoggedAt));
  const lastAutologgedAt = latest(scheduled.map((m) => m.lastAutologgedAt));
  return {
    basis,
    hasSchedule: scheduled.length > 0,
    lastLoggedAt,
    daysSinceLastData: daysSince(lastLoggedAt, patient.timezone, now),
    autologOn:
      lastAutologgedAt !== null &&
      (lastLoggedAt === null ||
        Date.parse(lastAutologgedAt) > Date.parse(lastLoggedAt))
  };
}

export function medicationName(
  runeClassification: string,
  catalog: MedicationOption[]
): string {
  const parts = runeClassification.split(".");
  const lastSegment = parts[parts.length - 1];

  const catalogMatch = catalog.find((m) => m.classification === lastSegment);
  if (catalogMatch) {
    return catalogMatch.name;
  }

  return lastSegment
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}
